import { dispatch } from '@src/dispatcher';

import CommonStore from '@src/stores/CommonStore';
import { CONNECTION_TYPE } from '@src/constants/MogamboConstants';
import * as ClusterApi from '@src/api';
import * as Utils from '@src/utils';

import ACTION_TYPES from './actionTypes';
import * as Api from './api';
import Store from './store';

const { getStateImmutable } = Store;

const { languageSource, languageScript } = CommonStore.getCurrentLanguage();

export function fetchConnectionData(
  sourceType,
  sourceID,
  destinationType,
  destinationID,
  cb = () => {}
) {
  return new Promise(async (resolve, reject) => {
    try {
      const connectionResponse = await Api.fetchConnectionData(
        sourceType,
        sourceID,
        destinationType,
        destinationID,
        languageSource,
        languageScript
      );

      const connectionType = `${sourceType}-${destinationType}`;

      const fromNode = {
        id: connectionResponse.body.from_id,
        name: connectionResponse.body.from_node_name,
        title: connectionResponse.body.from_title,
      };

      const toNode = {
        id: connectionResponse.body.to_id,
        name: connectionResponse.body.to_node_name,
        title: connectionResponse.body.to_title,
      };

      switch (connectionType) {
        case CONNECTION_TYPE.DYNAMIC_TO_STATIC:
        case CONNECTION_TYPE.STATIC_TO_STATIC:
          dispatch(ACTION_TYPES.RECEIVED_CONNECTION_DATA, {
            entityList: connectionResponse.body.connection_data.entity_list,
            transitionEntityList:
              connectionResponse.body.connection_data.transition_entity_list,
            transitionValueData:
              connectionResponse.body.connection_data.transition_value_data,
            transitionUserResponses:
              connectionResponse.body.connection_data.transition_user_responses,
            connectionFrom: fromNode,
            connectionTo: toNode,
            biDirectional: connectionResponse.body.bi_directional,
            domainSynonyms: connectionResponse.body.domain_synonyms || {},
            nodeEntities: connectionResponse.body.node_entities || {},
          });
          break;

        case CONNECTION_TYPE.STATIC_TO_CODE:
          dispatch(ACTION_TYPES.RECEIVED_CONNECTION_DATA, {
            connectionFrom: fromNode,
            connectionTo: toNode,
            biDirectional: connectionResponse.body.bi_directional,
          });
          break;

        case CONNECTION_TYPE.CODE_TO_DYNAMIC:
          dispatch(ACTION_TYPES.RECEIVED_CONNECTION_DATA, {
            rules: connectionResponse.body.connection_data.rules.rule_list || [
              {},
            ],
            clause:
              connectionResponse.body.connection_data.rules.clause || 'AND',
            connectionFrom: fromNode,
            connectionTo: toNode,
            biDirectional: connectionResponse.body.bi_directional,
            outputJson:
              connectionResponse.body.connection_data.output_json || {},
          });
          break;

        default:
          break;
      }

      resolve(connectionResponse);
    } catch (e) {
      reject(e);
      cb(false);
    }
  });
}

export function updateConnectionData(key, value, isValid = true) {
  dispatch(ACTION_TYPES.UPDATE_CONNECTION_DATA, { [key]: value, isValid });
}

export function saveConnectionData(
  sourceType,
  destinationType,
  actionType,
  cb = () => {},
  domainTitle = '',
  nodeName = ''
) {
  const store = getStateImmutable();
  const sourceID = store.connectionFrom.id;
  const destinationID = store.connectionTo.id;
  const data = {};
  const connectionData = {};

  const connectionType = `${sourceType}-${destinationType}`;

  // filters out empty new disambiguation response
  let { transitionUserResponses } = store;
  transitionUserResponses = transitionUserResponses.filter(
    (response) => response.title || !response.new
  );

  switch (connectionType) {
    case CONNECTION_TYPE.DYNAMIC_TO_STATIC:
    case CONNECTION_TYPE.STATIC_TO_STATIC:
      connectionData.entity_list = store.entityList;
      connectionData.transition_entity_list = store.transitionEntityList;
      connectionData.transition_value_data = store.transitionValueData;
      connectionData.transition_user_responses = transitionUserResponses;
      data.domain_synonyms = store.domainSynonyms;
      data.node_entities = store.nodeEntities;

      break;

    case CONNECTION_TYPE.CODE_TO_DYNAMIC:
      connectionData.rules = {};
      connectionData.rules.rule_list = store.rules;
      connectionData.rules.clause = store.clause;
      break;

    default:
      break;
  }

  data.action_type = actionType;
  data.lang_src = languageSource;
  data.lang_script = languageScript;
  data.connection_data = connectionData;

  return new Promise(async (resolve, reject) => {
    try {
      // we check if synonym cluster is edited, we convert them and make the API call
      // the conversion and setMeanings is an already existing function, API respectively.
      // we only notify the user if saving of cluster fails, and continue making rest of the calls
      if (store.isDomainSynonymsEdited) {
        const convertedSynonyms = Utils.convertClusters(
          domainTitle,
          store.domainSynonyms
        );

        await ClusterApi.setMeanings(convertedSynonyms);
      }

      // we check if node entities is edited, we convert them and make the API call
      // the conversion and setMeanings is an already existing function, API respectively.
      // we only notify the user if saving of cluster fails, and continue making rest of the calls
      if (store.isNodeEntitiesEdited) {
        const convertedEntities = Utils.convertEntities(
          nodeName,
          store.nodeEntities
        );

        await ClusterApi.setUserSaysEntities(convertedEntities);
      }

      const connectionResponse = await Api.saveConnectionData({
        sourceType,
        sourceID,
        destinationType,
        destinationID,
        data,
        domainTitle
      });

      dispatch(ACTION_TYPES.SAVE_CONNECTION_DATA);
      cb(true);
      resolve(connectionResponse);
    } catch (e) {
      cb(false);
      reject(e);
    }
  });
}

export function resetData() {
  dispatch(ACTION_TYPES.RESET_DATA);
}

export function createNewConnection(
  sourceType,
  sourceID,
  destinationType,
  destinationID,
  langSource='',
  langScript='',
  domainTitle='',
) {
  const data = {};
  data.action_type = 'update';
  data.lang_src = languageSource;
  data.lang_script = languageScript;
  data.connection_data = {};
  
  return new Promise(async (resolve, reject) => {
    try {
      const connectionResponse = await Api.saveConnectionData({
        sourceType,
        sourceID,
        destinationType,
        destinationID,
        data,
        domainTitle
      });

      resolve(connectionResponse);
    } catch (e) {
      reject(e);
    }
  });
}
